<template>
  <section class="px-1 pb-5 xl:pb-0">
    <div class="container">
      <div class="row items-center justify-center relative xl:block clearfix">
        <!-- bubble top -->
        <div class="pattern-2 pattern-2__intro embed-responsive embed-responsive-1by1 bg-light opacity-40 col-3 hidden md:block absolute" />
        <!-- bubble bottom -->
        <div class="pattern-1 pattern-1__intro embed-responsive embed-responsive-1by1 bg-light opacity-40 col-2 hidden md:block absolute" />
        <!-- image (gallery) -->
        <div class="sm:col-9 md:col-5 xl:col-6 xl:pl-2 xl:pr-5 order-1 md:order-0 float-left">
          <div class="hero-carousel">
            <div class="row overflow-hidden pb-7 lg:py-5 lg:mx-n6">
              <div
                class="row mx-0 w-100 mb-0 flex-nowrap flex-grow-1 h-auto list-unstyled"
              >
                <div class="col-12 lg:px-6">
                  <div class="hero-carousel__image mb-0">
                    <div
                      class="hero-carousel__image__in no-padding"
                    >
                      <RemoteResponsiveImage
                        :product-image-data="article.image"
                        img-class="hero-carousel__image__in-img"
                        :alt="article.title"
                        sizes="(min-width: 1200px) 620px, (min-width: 500px) 582px, 70vw"
                        :lazy-load="false"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Heading -->
        <div class="md:col-7 xl:col-6 xl:px-5 mb-4 md:mb-0 order-0 md:order-0 float-right clear-right">
          <BreadCrumb :breadcrumb-list="breadcrumbList" />

          <div class="text-muted flex text-sm mb-2">
            <strong
              v-for="(item, index) in article.tags"
              :key="index"
            >
              {{ item.name }}
              <span
                v-if="index < article.tags.length - 1"
                class="mx-1"
              >•</span>
            </strong>
          </div>
          <h1 class="heading-4 mb-4 md:mb-5">
            {{ article.title }}
          </h1>
          <BlogShare />
        </div>
        <!-- Short description -->
        <div class="xl:col-6 xl:px-5 order-2 md:order-0 float-right clear-right">
          <div class="text">
            <p class="mt-4">
              {{ article.introText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import BreadCrumb from '@/components/general/BaseBreadcrumb.vue'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import BlogShare from '@/components/blog/BlogShare.vue'
import useHead from '@/mixins/headManager'
import type { Article } from '@/types/articles'

const props = defineProps<{
  article: Article
  permalink: string
}>()

const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const jsonld = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  'headline': props.article.title,
  'image': props.article.image.llg.formats["image/jpeg"] ?? props.article.image.lg.formats["image/jpeg"],
  'author': {
    '@type': 'Person',
    'name': import.meta.env.VITE_VUE_APP_SITE_NAME,
    'url': baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl,
  },
}
useHead({
  title: props.article.title,
  meta: [
    {
      property: `og:title`,
      content: computed(() => props.article.title),
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(jsonld),
    },
  ],
})

const breadcrumbList = ref([
  {
    name: 'Blog',
    link: { name: 'blogPath' },
  },
  {
    name: props.article.title,
    link: { name: 'showBlogPath', params: { permalink: props.permalink } },
  },
])
</script>
